import { useDroppable } from '@dnd-kit/core'
import { Box, Card, CardContent, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { CarrierVisitStatus } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { SearchIcon, useMinimalsTheme } from '@tom-ui/ui'
import {
  extractRailcarPlanningNumberIdentifierByType,
  isRailcarPlanningIdentifierOfType,
  RailcarPlanningDraggableEnum,
  RailcarPlanningViewStore,
} from '../../Stores/RailcarPlanningViewStore'
import { RailcarPlanningContainerProps } from '../Molecules/RailcarPlanningContainer'
import { RailcarPlanningUnassignedContainerDroppableArea } from '../Molecules/RailcarPlanningDroppableArea'
import { RailcarPlanningDraggableContainer } from './RailcarPlanningDraggableContainer'

type RailcarPlanningDroppableContainerProps = {
  railcarPlanningViewStore: RailcarPlanningViewStore
  containers?: RailcarPlanningContainerProps[]
}

export const RailcarPlanningDroppableContainer = (
  props: RailcarPlanningDroppableContainerProps,
) => {
  const { railcarPlanningViewStore, containers } = props
  const { setNodeRef, active, over, isOver } = useDroppable({
    id: `${RailcarPlanningDraggableEnum.UNASSIGNED}:1`,
    disabled: railcarPlanningViewStore.railVisitItem.data.status === CarrierVisitStatus.Closed,
  })
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const isSelectedContainerDeparted = railcarPlanningViewStore.isSelectedContainerDeparted(
    active?.id,
  )

  const isDragginContainer =
    !!active?.id &&
    isRailcarPlanningIdentifierOfType(active.id, RailcarPlanningDraggableEnum.CONTAINER)

  const isOverContainerInUnassignedArea =
    !!over?.id &&
    isRailcarPlanningIdentifierOfType(over.id, RailcarPlanningDraggableEnum.CONTAINER) &&
    containers?.some(
      container =>
        container.id ===
        extractRailcarPlanningNumberIdentifierByType(
          over!.id,
          RailcarPlanningDraggableEnum.CONTAINER,
        ),
    )

  const showDroppableArea = (isOver || isOverContainerInUnassignedArea) && isDragginContainer

  return (
    <Card style={{ height: '100%' }} ref={setNodeRef}>
      <CardContent style={{ height: '100%' }}>
        <Typography variant='h4' sx={{ pt: theme.customSpacing.xs, pb: theme.customSpacing.xs }}>
          {t('containers', 'Containers')}
        </Typography>
        {!!containers && containers.length > 0 && (
          <Stack>
            <TextField
              size='small'
              variant='outlined'
              placeholder={t('searchForContainer', 'Search for container')}
              onChange={val => railcarPlanningViewStore.setContainerFilter(val.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ width: '100%' }}
            />
            <Box
              sx={{
                overflow: 'auto',
                height: 'calc(100% - 112px)',
              }}
            >
              <Stack
                spacing={1}
                mt={theme.customSpacing.m}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2,
                }}
              >
                {containers?.map(item => (
                  <Stack key={item.id} sx={{ marginTop: '0 !important' }}>
                    <RailcarPlanningDraggableContainer
                      container={item}
                      railcarPlanningViewStore={railcarPlanningViewStore}
                    />
                  </Stack>
                ))}
                {showDroppableArea && (
                  <RailcarPlanningUnassignedContainerDroppableArea
                    isDepartedContainer={isSelectedContainerDeparted}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        )}
        {(!containers || containers.length === 0) && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {showDroppableArea && (
              <RailcarPlanningUnassignedContainerDroppableArea
                isDepartedContainer={isSelectedContainerDeparted}
              />
            )}

            {!showDroppableArea && (
              <>
                <Typography variant='subtitle1' textAlign='center'>
                  {t('railcarPlanningContainerListEmptyText1', 'Your container list is empty')}.
                </Typography>
                <Typography variant='body1' textAlign='center'>
                  {t(
                    'railcarPlanningContainerListEmptyText2',
                    'Add containers manually or upload a file to fill this list',
                  )}
                  .
                </Typography>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
